/**
 * Animation Selector Component
 * Allows user to choose animation intensity level
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  Chip,
  Grid
} from '@mui/material';

const AnimationLevels = [
  {
    value: 0,
    id: 'none',
    label: 'None',
    description: 'No animations - fastest loading',
    details: 'Static content only, best for performance-critical sites',
    icon: '⚡'
  },
  {
    value: 1,
    id: 'minimal',
    label: 'Minimal',
    description: 'Subtle transitions only',
    details: 'Basic hover effects and smooth scrolling',
    icon: '🌊'
  },
  {
    value: 2,
    id: 'subtle',
    label: 'Subtle',
    description: 'Gentle animations (recommended)',
    details: 'Fade-ins, smooth transitions, professional feel',
    icon: '✨'
  },
  {
    value: 3,
    id: 'modern',
    label: 'Modern',
    description: 'Noticeable but tasteful',
    details: 'Slide-ins, parallax effects, engaging interactions',
    icon: '🎭'
  },
  {
    value: 4,
    id: 'dynamic',
    label: 'Dynamic',
    description: 'Bold and eye-catching',
    details: 'Complex animations, attention-grabbing effects',
    icon: '🚀'
  }
];

const AnimationSelector = ({ value, onChange }) => {
  // Convert string value to number for slider
  const currentLevel = AnimationLevels.find(level => level.id === value) || AnimationLevels[2];
  const sliderValue = currentLevel.value;

  const handleSliderChange = (event, newValue) => {
    const selectedLevel = AnimationLevels.find(level => level.value === newValue);
    onChange(selectedLevel.id);
  };

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Animation Level
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how much motion you want on your website
      </Typography>

      {/* Slider Control */}
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ px: 2 }}>
          <Slider
            value={sliderValue}
            onChange={handleSliderChange}
            step={1}
            marks={AnimationLevels.map(level => ({
              value: level.value,
              label: level.icon
            }))}
            min={0}
            max={4}
            valueLabelDisplay="off"
            sx={{
              '& .MuiSlider-mark': {
                height: 12,
                width: 12,
                borderRadius: '50%',
                backgroundColor: 'primary.main'
              },
              '& .MuiSlider-markLabel': {
                fontSize: 20,
                top: -30
              }
            }}
          />
        </Box>

        {/* Current Selection Display */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {currentLevel.label}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentLevel.description}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {currentLevel.details}
          </Typography>
        </Box>
      </Card>

      {/* Animation Level Cards */}
      <Grid container spacing={2}>
        {AnimationLevels.map((level) => (
          <Grid item xs={12} sm={6} md={4} key={level.id}>
            <Card
              sx={{
                cursor: 'pointer',
                border: value === level.id ? '2px solid' : '1px solid',
                borderColor: value === level.id ? 'primary.main' : 'divider',
                bgcolor: value === level.id ? 'primary.50' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 2
                }
              }}
              onClick={() => onChange(level.id)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="h4" sx={{ mr: 1 }}>
                    {level.icon}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {level.label}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {level.description}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {level.details}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recommendations */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" fontWeight="medium" gutterBottom>
          💡 Recommendations:
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Chip 
              label="Professional/Corporate: Minimal or Subtle" 
              size="small" 
              variant="outlined" 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Chip 
              label="Creative/Modern: Modern or Dynamic" 
              size="small" 
              variant="outlined" 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Chip 
              label="Medical/Legal: Minimal or Subtle" 
              size="small" 
              variant="outlined" 
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Chip 
              label="Restaurant/Photography: Subtle or Modern" 
              size="small" 
              variant="outlined" 
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AnimationSelector;
